/* eslint-disable camelcase */
import {
  PaymentRequestPaymentMethodEvent,
  PaymentRequestShippingAddress,
  PaymentRequestShippingAddressEvent,
  PaymentRequestShippingOption,
  PaymentRequestShippingOptionEvent,
} from '@stripe/stripe-js';
import { ICartModel } from '../../../models/stores/Cart';
import { AddressContract } from '../../../types/Address';
import { CartUpdateContract } from '../../../types/Cart';
import { ShippingMethodContract } from '../../../types/ShippingMethods';
import { RootStoreInstance } from '../../../models/stores/Root';
import { IntentUpdateContract } from '../../../types/Stripe';

let lastAddress: AddressContract;
let lastShippingMethods: ShippingMethodContract[];

const getAddressUpdate = (
  shippingAddress: PaymentRequestShippingAddress
): AddressContract => {
  return {
    id: null,
    firstName: null,
    lastName: null,
    companyName: null,
    phoneNumber: null,
    preferred: false,
    address1: shippingAddress.addressLine?.[0] ?? null,
    address2: shippingAddress.addressLine?.[1] ?? null,
    city: shippingAddress.city ?? null,
    state: shippingAddress.region ?? null,
    postalCode: shippingAddress.postalCode ?? null,
    country: shippingAddress.country ?? null,
    residential: true,
  };
};

const extractShippingOptionsFromCartData = (
  cartData: CartUpdateContract
): PaymentRequestShippingOption[] => {
  return cartData?.shippingMethods
    ?.sort(a => (a.selected ? -1 : 0))
    .map(shippingMethodData => {
      return {
        id: shippingMethodData.id.toFixed(0),
        label: shippingMethodData.name,
        amount: Math.round(shippingMethodData.price * 100),
        detail: '',
      };
    });
};

const extractCartSubTotal = (cartData: CartUpdateContract) => {
  const { items, discounts, giftWrap, kitItems } = cartData.totals;
  return Math.round((items + discounts + giftWrap + kitItems) * 100);
};

const extractCartShippingTotal = (cartData: CartUpdateContract) => {
  return Math.round(cartData.totals.shipping * 100);
};

const extractCartTaxTotal = (cartData: CartUpdateContract) => {
  const { tax1, tax2, tax3 } = cartData.totals;
  return Math.round((tax1 + tax2 + tax3) * 100);
};

const extractCartGrandTotal = (cartData: CartUpdateContract) => {
  return Math.round(cartData.totals.grandTotal * 100);
};

const formatCartTotalBreakdown = (cartData: CartUpdateContract) => {
  return [
    {
      label: 'Subtotal',
      amount: extractCartSubTotal(cartData),
    },
    {
      label: 'Shipping',
      amount: extractCartShippingTotal(cartData),
    },
    {
      label: 'Tax',
      amount: extractCartTaxTotal(cartData),
    },
  ];
};

const formatCartTotal = (cartData: CartUpdateContract) => {
  return {
    label: 'Total',
    amount: extractCartGrandTotal(cartData),
  };
};

const formatUpdateParams = (
  event: PaymentRequestPaymentMethodEvent
): IntentUpdateContract => {
  const {
    shippingAddress,
    payerPhone: phone,
    shippingOption,
    paymentMethod,
  } = event;
  const {
    recipient: name,
    addressLine,
    city,
    region: state,
    postalCode: postal_code,
    country,
  } = shippingAddress as PaymentRequestShippingAddress;
  return {
    payment_method: paymentMethod.id,
    shipping: {
      name: name as string,
      carrier: (shippingOption as PaymentRequestShippingOption).id,
      phone: phone as string,
      address: {
        line1: addressLine?.[0] ?? '',
        line2: addressLine?.[1] ?? '',
        city: city ?? '',
        state: state ?? '',
        postal_code: postal_code ?? '',
        country: country ?? '',
      },
    },
  };
};

const handleShippingAddressChange = async (
  event: PaymentRequestShippingAddressEvent,
  cart: ICartModel
): Promise<void> => {
  const { shippingAddress, updateWith } = event;
  const { cartData } = cart;
  const cartService = cart.getCartService();
  cartData.shippingAddress = getAddressUpdate(shippingAddress);
  lastAddress = { ...cartData.shippingAddress };
  let result = await cartService.postCartDataAsync(cart.cartId, cartData);

  if (
    result.shippingMethods.length &&
    !result?.shippingMethods.some(method => method.selected)
  ) {
    result.shippingMethods[0].selected = true;
    result = await cartService.postCartDataAsync(cart.cartId, result);
  }
  lastShippingMethods = result?.shippingMethods;

  updateWith({
    status: 'success',
    displayItems: formatCartTotalBreakdown(result),
    total: formatCartTotal(result),
    shippingOptions: extractShippingOptionsFromCartData(result),
  });
};

const handleShippingOptionChange = async (
  event: PaymentRequestShippingOptionEvent,
  cart: ICartModel
): Promise<void> => {
  const { shippingOption, updateWith } = event;
  const { cartData } = cart;
  const cartService = cart.getCartService();
  cartData.shippingAddress = lastAddress;
  cartData.shippingMethods = lastShippingMethods.map(method => {
    return {
      ...method,
      selected: method.id.toFixed(0) === shippingOption.id,
    };
  });
  const result = await cartService.postCartDataAsync(cart.cartId, cartData);
  updateWith({
    status: 'success',
    displayItems: formatCartTotalBreakdown(result),
    total: formatCartTotal(result),
  });
};

const handlePaymentMethod = async (
  event: PaymentRequestPaymentMethodEvent,
  root: RootStoreInstance
): Promise<void> => {
  const updateParams = formatUpdateParams(event);
  const stripeService = root.getStripeService();
  return stripeService.postIntent(updateParams, root.cart.updateStripeInfo);
};

const getLastShippingMethods = (): ShippingMethodContract[] => {
  return lastShippingMethods ?? [];
};

export {
  handleShippingAddressChange,
  handleShippingOptionChange,
  handlePaymentMethod,
  getAddressUpdate,
  extractCartGrandTotal,
  extractCartShippingTotal,
  extractCartSubTotal,
  extractCartTaxTotal,
  extractShippingOptionsFromCartData,
  formatCartTotal,
  formatCartTotalBreakdown,
  formatUpdateParams,
  getLastShippingMethods,
};
